'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { ChevronDownIcon, ExitIcon, IdCardIcon } from '@radix-ui/react-icons';
import {
  NavigationMenu,
  NavigationMenuLink,
} from '@radix-ui/react-navigation-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import React from 'react';

import { redirectToSuitableSignUpFunnelStep } from '@/app/[locale]/(legacy)/(auth)/sign-up/action';
import { Icons } from '@/app/_components/ui/icons';
import { PersonIcon } from '@/app/_componentsv2/icons/person-icon';
import {
  accountNav,
  accountNavItem,
  accountNavItems,
  accountNavLink,
  accountNavLinkIcon,
  desktop,
  mainNavDropdown,
  mainNavSubItemLink,
} from '@/app/_componentsv2/layout/main/main-header.css';
import { tooltipContent } from '@/app/_componentsv2/ui.css';
import { User } from '@/domain/user/services/types';
import { shutdownIntercom } from '@/lib/intercom/client';
import generatePath from '@/lib/router/generate-path';
import Routes from '@/lib/router/routes';
import { pathnames } from '@/lib/translation/config';

import { logout } from './action';

export type MenuItem = {
  key: string;
  path: keyof typeof pathnames | Routes | string;
  label: string;
  Icon?: React.ReactNode;
  hide?: boolean;
};

type ConnectedMenuProps = {
  user: User | undefined;
  hasUserInvestmentToSign: boolean;
};

export const useConnectedMenu = ({
  user,
}: ConnectedMenuProps): Array<MenuItem> => {
  const t = useTranslations();

  const menuItems = [
    {
      key: 'portfolio',
      path: generatePath(Routes.Portfolio),
      label: t('header.menu.portfolio'),
      Icon: <Icons.Document />,
    },
    {
      key: 'favorite',
      path: generatePath(Routes.FavoriteProjects),
      label: t('header.menu.favorite'),
      Icon: <Icons.Star />,
    },
    {
      key: 'transactions',
      path: generatePath(Routes.Transactions),
      label: t('header.menu.transactions'),
      Icon: <ExitIcon />,
    },
    {
      key: 'wallet',
      path: generatePath(Routes.Wallet),
      label: t('header.menu.wallet'),
      Icon: <Icons.CreditCard />,
    },
    {
      key: 'rib',
      path: generatePath(Routes.Rib),
      label: t('header.menu.rib'),
      Icon: <Icons.Edit />,
    },
    {
      key: 'company',
      path: generatePath(Routes.Company),
      label: t('header.menu.company'),
      Icon: <Icons.Building />,
      hide: !user?.isCompany,
    },
    {
      key: 'details',
      path: generatePath(Routes.Profile),
      label: t('header.menu.details'),
      Icon: <Icons.Account />,
    },
  ];

  return menuItems.filter((menuItem) => !menuItem.hide);
};

export function ConnectedMenu(props: ConnectedMenuProps) {
  const pathname = usePathname();
  const { user, hasUserInvestmentToSign } = props;

  const connectedMenu = useConnectedMenu(props);
  const t = useTranslations();

  const [portfolioMenuItem, ...dropdownMenuItems] = connectedMenu;

  const showCompleteFolderCta =
    !!user && (!user.isFolderCompleted || !user.isIdentityValidated);

  const searchParams =
    Routes.Home !== pathname
      ? `?${new URLSearchParams({ origin: pathname }).toString()}`
      : '';

  const handleCompleteFolder = async () => {
    await redirectToSuitableSignUpFunnelStep();
  };

  const handleLogout = async () => {
    // Here we need to manually call the intercom shutdown, because it must remove its own cookies before logging out the user
    shutdownIntercom();
    await logout();
  };

  if (!user) {
    return (
      <nav className={clsx(accountNav, desktop)} aria-label="Account">
        <ul className={accountNavItems}>
          <li className={accountNavItem}>
            <Link
              className={accountNavLink}
              href={`${Routes.SignIn}${searchParams}`}
            >
              <PersonIcon className={accountNavLinkIcon} />
              <span>{t('auth.signIn.title')}</span>
            </Link>
          </li>
        </ul>
      </nav>
    );
  }

  const portfolio = hasUserInvestmentToSign ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavigationMenuLink
            className={accountNavLink}
            href={portfolioMenuItem.path}
          >
            {portfolioMenuItem.label}
          </NavigationMenuLink>
        </TooltipTrigger>
        <TooltipContent className={tooltipContent} align="end" side="bottom">
          {t('header.menu.portfolioTooltip')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <NavigationMenuLink
      className={accountNavLink}
      href={portfolioMenuItem.path}
    >
      {portfolioMenuItem.label}
    </NavigationMenuLink>
  );

  return (
    <NavigationMenu
      className={clsx(accountNavItems, desktop)}
      aria-label="Account"
    >
      {portfolio}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={accountNavLink}>
            {user.firstname && user.lastname
              ? `${user.firstname} ${user.lastname.charAt(0).toUpperCase()}.`
              : null}
            <ChevronDownIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={mainNavDropdown}
          onCloseAutoFocus={(e) => e.preventDefault()}
          align="end"
        >
          {dropdownMenuItems.map((item) => (
            <React.Fragment key={item.key}>
              {item.key === 'details' && !user.isVerified ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        className={clsx(mainNavSubItemLink, {
                          '': item.path === pathname,
                        })}
                        asChild
                      >
                        <Link href={item.path}>
                          {item.Icon ? item.Icon : null}
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent
                      align="center"
                      side="left"
                      className={tooltipContent}
                    >
                      {t('header.menu.detailsTooltip')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <DropdownMenuItem
                  className={clsx(mainNavSubItemLink, {
                    '': item.path === pathname,
                  })}
                  asChild
                >
                  <Link href={item.path}>
                    {item.Icon ? item.Icon : null}
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              )}
              {['favorite', 'wallet'].includes(item.key) ? (
                <DropdownMenuSeparator />
              ) : null}
            </React.Fragment>
          ))}
          {showCompleteFolderCta ? (
            <DropdownMenuItem onClick={() => handleCompleteFolder()}>
              <IdCardIcon />
              {t('header.menu.complete-folder')}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className={mainNavSubItemLink}
            onClick={() => handleLogout()}
          >
            {t('auth.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </NavigationMenu>
  );
}
